import { useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Minus, ArrowRight, ChevronDown, X } from 'lucide-react';

const T = { yellow: 1000000, orange: 5000000, red: 20000000 };
const MIN_NOTABLE = 100000; // Ignore changes below 100k
const MIN_BLOOM = 500000;   // Minimum value to consider a bloom
const NB_M = ['jan','feb','mar','apr','mai','jun','jul','aug','sep','okt','nov','des'];
const fmtD = (s) => { if (!s) return ''; const d = new Date(s); return isNaN(d) ? s : `${d.getDate()}. ${NB_M[d.getMonth()]}`; };
const fmtV = (v) => { if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`; if (v >= 1e3) return `${Math.round(v/1e3)}k`; return '~0'; };
const parseN = (str) => {
  if (!str) return 0;
  const s = String(str).trim();
  if (s.startsWith('<') || s === '' || s === '0') return 0;
  return parseFloat(s.replace(/[\s\u00A0]/g, '').replace(/,/g, '.')) || 0;
};
const zone = (v) => { if (v >= T.red) return 'red'; if (v >= T.orange) return 'orange'; if (v >= T.yellow) return 'yellow'; return 'green'; };

const STYLES = {
  critical: { bg: 'bg-red-50 border-red-200/60', icon: AlertTriangle, iconBg: 'bg-red-100', iconC: 'text-red-600', title: 'text-red-800', sub: 'text-red-600', val: 'text-red-700' },
  warning:  { bg: 'bg-amber-50 border-amber-200/60', icon: TrendingUp, iconBg: 'bg-amber-100', iconC: 'text-amber-600', title: 'text-amber-800', sub: 'text-amber-600', val: 'text-amber-700' },
  rising:   { bg: 'bg-blue-50 border-blue-200/60', icon: TrendingUp, iconBg: 'bg-blue-100', iconC: 'text-blue-600', title: 'text-blue-800', sub: 'text-blue-600', val: 'text-blue-700' },
  falling:  { bg: 'bg-emerald-50 border-emerald-200/60', icon: TrendingDown, iconBg: 'bg-emerald-100', iconC: 'text-emerald-600', title: 'text-emerald-800', sub: 'text-emerald-600', val: 'text-emerald-700' },
  calm:     { bg: 'bg-slate-50 border-slate-200/60', icon: CheckCircle, iconBg: 'bg-emerald-100', iconC: 'text-emerald-600', title: 'text-slate-800', sub: 'text-slate-500', val: 'text-slate-600' },
};

export default function TrendAlerts({ allBulks, selectedAreas }) {
  const [expanded, setExpanded] = useState(false);
  const result = useMemo(() => {
    if (!allBulks || allBulks.length < 2) return null;

    const bulks = selectedAreas.length === 0 ? allBulks : allBulks.filter(b => selectedAreas.includes(b.area_name));
    const dates = [...new Set(bulks.map(b => b.date))].sort((a, b) => b.localeCompare(a));
    if (dates.length < 2) return null;

    const latestDate = dates[0];
    const prevDate = dates[1];
    const latest = bulks.filter(b => b.date === latestDate);
    const prev = bulks.filter(b => b.date === prevDate);

    // Build per-location organism values
    const buildLocMap = (bulkList) => {
      const map = {}; // { "loc": { "orgKey": numVal } }
      bulkList.forEach(bulk => {
        if (!bulk.algae_data) return;
        Object.entries(bulk.algae_data).forEach(([orgKey, locData]) => {
          if (!orgKey.startsWith('Kiselalger_')) return;
          Object.entries(locData).forEach(([loc, val]) => {
            const cleanLoc = loc.includes(' - ') ? loc.split(' - ').pop() : loc;
            if (!map[cleanLoc]) map[cleanLoc] = {};
            map[cleanLoc][orgKey] = parseN(val);
          });
        });
      });
      return map;
    };

    const latestMap = buildLocMap(latest);
    const prevMap = buildLocMap(prev);

    const events = [];

    Object.entries(latestMap).forEach(([loc, organisms]) => {
      Object.entries(organisms).forEach(([orgKey, curVal]) => {
        const species = orgKey.split('_').slice(1).join('_');
        if (species.toUpperCase().includes('SUM')) return; // Handle SUM separately
        
        const prevVal = prevMap[loc]?.[orgKey] || 0;
        const z = zone(curVal);
        const prevZ = zone(prevVal);
        const diff = curVal - prevVal;
        const pct = prevVal > 0 ? (diff / prevVal) * 100 : 0;

        // 1. Over raud grense
        if (z === 'red') {
          events.push({ severity: 100, type: 'critical', loc, species, curVal, prevVal, 
            msg: `${species} over raud grense`, detail: `${fmtV(prevVal)} → ${fmtV(curVal)} ved ${loc}` });
        }
        // 2. In oransje sone
        else if (z === 'orange') {
          events.push({ severity: 80, type: 'critical', loc, species, curVal, prevVal,
            msg: `${species} i oransje sone`, detail: `${fmtV(prevVal)} → ${fmtV(curVal)} ved ${loc}` });
        }
        // 3. In gul sone and rising
        else if (z === 'yellow' && diff > 0) {
          events.push({ severity: 60, type: 'warning', loc, species, curVal, prevVal,
            msg: `${species} aukar i gul sone`, detail: `${fmtV(prevVal)} → ${fmtV(curVal)} ved ${loc} (+${Math.round(pct)}%)` });
        }
        // 4. In gul sone stable/high
        else if (z === 'yellow') {
          events.push({ severity: 50, type: 'warning', loc, species, curVal, prevVal,
            msg: `${species} framleis i gul sone`, detail: `${fmtV(curVal)} ved ${loc}` });
        }
        // 5. Significant bloom: large absolute increase and now above bloom threshold
        else if (curVal >= MIN_BLOOM && diff > MIN_NOTABLE && pct > 50) {
          events.push({ severity: 35, type: 'rising', loc, species, curVal, prevVal,
            msg: `${species} aukande oppblomstring`, detail: `${fmtV(prevVal)} → ${fmtV(curVal)} ved ${loc} (+${Math.round(pct)}%)` });
        }
        // 6. Significant decline from above threshold
        else if (prevZ !== 'green' && z === 'green' && prevVal >= MIN_BLOOM) {
          events.push({ severity: 20, type: 'falling', loc, species, curVal, prevVal,
            msg: `${species} tilbake i grøn sone`, detail: `${fmtV(prevVal)} → ${fmtV(curVal)} ved ${loc}` });
        }
      });

      // Check SUM Kiselalger
      const sumKey = Object.keys(organisms).find(k => k.toUpperCase().includes('SUM'));
      if (sumKey) {
        const curSum = organisms[sumKey];
        const prevSum = prevMap[loc]?.[sumKey] || 0;
        const zS = zone(curSum);
        if (zS === 'red') {
          events.push({ severity: 95, type: 'critical', loc, species: 'SUM Kiselalgar', curVal: curSum, prevVal: prevSum,
            msg: `SUM Kiselalgar over raud grense`, detail: `${fmtV(curSum)} ved ${loc}` });
        } else if (zS === 'orange') {
          events.push({ severity: 75, type: 'critical', loc, species: 'SUM Kiselalgar', curVal: curSum, prevVal: prevSum,
            msg: `SUM Kiselalgar i oransje sone`, detail: `${fmtV(prevSum)} → ${fmtV(curSum)} ved ${loc}` });
        } else if (zS === 'yellow') {
          events.push({ severity: 45, type: 'warning', loc, species: 'SUM Kiselalgar', curVal: curSum, prevVal: prevSum,
            msg: `SUM Kiselalgar i gul sone`, detail: `${fmtV(curSum)} ved ${loc}` });
        }
      }
    });

    events.sort((a, b) => b.severity - a.severity);
    return { events, latestDate, prevDate };
  }, [allBulks, selectedAreas]);

  if (!result) return null;
  const { events, latestDate, prevDate } = result;

  if (events.length === 0) {
    const s = STYLES.calm;
    const Icon = s.icon;
    return (
      <div className="mb-5" data-testid="trend-alerts">
        <div className={`flex items-center gap-3 px-5 py-4 border rounded-2xl ${s.bg}`}>
          <div className={`w-9 h-9 ${s.iconBg} rounded-xl flex items-center justify-center shrink-0`}>
            <Icon className={`w-4.5 h-4.5 ${s.iconC}`} />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-semibold ${s.title}`}>Roleg situasjon</p>
            <p className={`text-xs ${s.sub}`}>Alle kiselalgar under gul grenseverdi · {fmtD(prevDate)} → {fmtD(latestDate)}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-5" data-testid="trend-alerts">
      {/* Summary row - clickable to expand */}
      {(() => {
        const criticalCount = events.filter(e => e.type === 'critical').length;
        const warningCount = events.filter(e => e.type === 'warning').length;
        const risingCount = events.filter(e => e.type === 'rising').length;
        const fallingCount = events.filter(e => e.type === 'falling').length;
        const topEvent = events[0];
        const s = STYLES[topEvent.type];
        const Icon = s.icon;
        
        return (
          <>
            <button
              onClick={() => setExpanded(true)}
              className={`w-full flex items-center gap-3 px-5 py-3.5 border rounded-xl ${s.bg} hover:opacity-90 transition-opacity cursor-pointer text-left`}
              data-testid="trend-alerts-summary"
            >
              <div className={`w-9 h-9 ${s.iconBg} rounded-xl flex items-center justify-center shrink-0`}>
                <Icon className={`w-4.5 h-4.5 ${s.iconC}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${s.title}`}>
                  {events.length} {events.length === 1 ? 'varsling' : 'varslingar'}
                  {criticalCount > 0 && <span className="ml-1.5 text-xs font-medium text-red-600">({criticalCount} kritisk)</span>}
                </p>
                <p className={`text-xs ${s.sub} truncate`}>
                  {topEvent.loc} — {topEvent.msg}
                  {events.length > 1 && ` og ${events.length - 1} til`}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {criticalCount > 0 && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full">{criticalCount}</span>}
                {warningCount > 0 && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">{warningCount}</span>}
                {(risingCount + fallingCount) > 0 && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">{risingCount + fallingCount}</span>}
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </div>
            </button>

            {/* Modal with all alerts */}
            {expanded && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setExpanded(false)}>
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div>
                      <h3 className="text-base font-bold text-slate-900">Trendvarslingar</h3>
                      <p className="text-xs text-slate-500">{fmtD(prevDate)} → {fmtD(latestDate)}</p>
                    </div>
                    <button onClick={() => setExpanded(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="overflow-y-auto max-h-[60vh] p-3 space-y-1.5">
                    {events.map((ev, i) => {
                      const evS = STYLES[ev.type];
                      const EvIcon = evS.icon;
                      return (
                        <div key={`${ev.loc}-${ev.species}-${i}`} className={`flex items-center gap-3 px-4 py-3 border rounded-xl ${evS.bg}`}>
                          <div className={`w-8 h-8 ${evS.iconBg} rounded-lg flex items-center justify-center shrink-0`}>
                            <EvIcon className={`w-4 h-4 ${evS.iconC}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${evS.title}`}>{ev.loc} — {ev.msg}</p>
                            <p className={`text-xs ${evS.sub}`}>{ev.detail}</p>
                          </div>
                          <div className="shrink-0 text-right">
                            <span className={`text-base font-bold font-mono ${evS.val}`}>{fmtV(ev.curVal)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
                    <p className="text-[10px] text-slate-400">Ignorerer verdiar under 100k</p>
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}
